import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Target, Activity, AlertCircle, CheckCircle, Home, CreditCard, Bitcoin, Wallet, PieChart as PieChartIcon } from 'lucide-react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { useCountry } from '../CountryContext';
import { formatCurrency, getCurrencySymbol, getCurrencyCode } from '../countryConfig';
import { formatChartValue } from '../utils/chartFormatters';
import {
  calculateNetWorth,
  groupLiabilitiesByType,
  calculateDebtToAssetRatio,
  calculateFinancialHealthScore
} from '../utils/netWorthCalculations';
import { groupInvestmentsByCategory } from '../utils/accountUtils';
import { useCryptoPrice } from '../useCryptoPrice';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { usePortfolioPrices } from '../hooks/usePricing';

const NetWorthDashboard = ({ isDark, investments = [], retirementGoals = {} }) => {
  const { currentUser } = useAuth();
  const { selectedCountry } = useCountry();

  // ⭐ MULTI-CURRENCY: Load exchange rates
  const { rates: exchangeRates, loading: ratesLoading } = useExchangeRates();
  const displayCurrency = getCurrencyCode(selectedCountry);
  const [liabilities, setLiabilities] = useState([]);
  const [cryptoHoldings, setCryptoHoldings] = useState([]);
  const [loading, setLoading] = useState(true);

  // ⭐ LIVE PRICING: Fetch live prices for investments
  const { prices: priceMap, loading: pricesLoading } = usePortfolioPrices(investments);

  // Load crypto prices
  const btcPrice = useCryptoPrice('bitcoin');
  const ethPrice = useCryptoPrice('ethereum');

  // Load data
  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load liabilities
      const liabilitiesQuery = query(
        collection(db, 'users', currentUser.uid, 'liabilities')
      );
      const liabilitiesSnapshot = await getDocs(liabilitiesQuery);
      const loadedLiabilities = [];
      liabilitiesSnapshot.forEach((doc) => {
        loadedLiabilities.push({ id: doc.id, ...doc.data() });
      });
      setLiabilities(loadedLiabilities);

      // Load crypto holdings
      const cryptoQuery = query(
        collection(db, 'users', currentUser.uid, 'cryptoHoldings')
      );
      const cryptoSnapshot = await getDocs(cryptoQuery);
      const loadedCrypto = [];
      cryptoSnapshot.forEach((doc) => {
        loadedCrypto.push({ id: doc.id, ...doc.data() });
      });
      setCryptoHoldings(loadedCrypto);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update crypto holdings with current prices
  const cryptoWithCurrentPrices = cryptoHoldings.map(holding => {
    let currentPrice = holding.currentPrice || 0;

    if (holding.symbol === 'BTC' && btcPrice) {
      currentPrice = btcPrice;
    } else if (holding.symbol === 'ETH' && ethPrice) {
      currentPrice = ethPrice;
    }

    return {
      ...holding,
      currentPrice,
      quantity: parseFloat(holding.quantity) || 0
    };
  });

  // Calculate net worth
  const cashReserves = parseFloat(retirementGoals.currentSavings) || 0;
  // ⭐ MULTI-CURRENCY + LIVE PRICING: Pass exchange rates, currency, and live prices
  const netWorthData = calculateNetWorth({
    investments,
    cryptoHoldings: cryptoWithCurrentPrices,
    liabilities,
    cashReserves,
    displayCurrency, // ⭐ Pass display currency
    exchangeRates,   // ⭐ Pass exchange rates for conversion
    priceMap         // ⭐ LIVE PRICING: Pass live prices
  });

  const {
    totalAssets,
    totalInvestments,
    totalCrypto,
    totalCash,
    totalLiabilities,
    netWorth
  } = netWorthData;

  // Calculate ratios and health score
  const debtToAssetRatio = calculateDebtToAssetRatio(totalAssets, totalLiabilities);
  const healthScore = calculateFinancialHealthScore({
    netWorth,
    totalAssets,
    totalLiabilities
  });

  // Prepare chart data - Group by 6 asset classes
  // ⭐ MULTI-CURRENCY + LIVE PRICING: Pass exchange rates and live prices
  const investmentsByCategory = groupInvestmentsByCategory(investments, displayCurrency, exchangeRates, priceMap);

  // Define asset class colors
  const assetClassColors = {
    'Stocks': '#3b82f6',        // Blue
    'Bonds': '#8b5cf6',          // Purple
    'Real Estate': '#10b981',    // Emerald green
    'Commodities': '#facc15',    // Bright yellow (gold)
    'Cryptocurrency': '#ec4899', // Pink/magenta
    'Cash': '#fb923c'            // Coral orange
  };

  // Build asset breakdown with all 6 classes
  // Combine values from investments array AND other sources (crypto holdings, cash reserves)
  const assetBreakdownData = [
    {
      name: 'Stocks',
      value: investmentsByCategory['Stocks']?.totalValue || 0,
      color: assetClassColors['Stocks']
    },
    {
      name: 'Bonds',
      value: investmentsByCategory['Bonds']?.totalValue || 0,
      color: assetClassColors['Bonds']
    },
    {
      name: 'Real Estate',
      value: investmentsByCategory['Real Estate']?.totalValue || 0,
      color: assetClassColors['Real Estate']
    },
    {
      name: 'Commodities',
      value: investmentsByCategory['Commodities']?.totalValue || 0,
      color: assetClassColors['Commodities']
    },
    {
      name: 'Cryptocurrency',
      // Combine crypto from investments array + crypto holdings collection
      value: (investmentsByCategory['Cryptocurrency']?.totalValue || 0) + totalCrypto,
      color: assetClassColors['Cryptocurrency']
    },
    {
      name: 'Cash',
      // Combine cash from investments array + cash reserves
      value: (investmentsByCategory['Cash']?.totalValue || 0) + totalCash,
      color: assetClassColors['Cash']
    }
  ].filter(item => item.value > 0);

  // ⭐ MULTI-CURRENCY: Pass exchange rates for proper currency conversion
  const liabilitiesBreakdownData = Object.values(groupLiabilitiesByType(liabilities, displayCurrency, exchangeRates))
    .map((group, index) => ({
      name: group.type,
      value: group.total,
      color: ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16'][index % 5]
    }))
    .filter(item => item.value > 0);

  // Assets vs Liabilities chart data
  // Ensure small values are visible by using a minimum display value when there's a large disparity
  const netWorthBreakdownData = [
    { name: 'Assets', value: totalAssets, displayValue: totalAssets, color: '#10b981' },
    { name: 'Liabilities', value: totalLiabilities, displayValue: totalLiabilities, color: '#ef4444' }
  ];

  const netWorthColor = netWorth >= 0 ? '#10b981' : '#ef4444';

  // ⭐ MULTI-CURRENCY + LIVE PRICING: Show loading while fetching data, exchange rates, or prices
  if (loading || ratesLoading || pricesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="ml-4 text-gray-600">
          {ratesLoading ? 'Loading exchange rates...' : pricesLoading ? 'Loading live prices...' : 'Loading financial data...'}
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Main Net Worth Card */}
      <div className={`p-8 rounded-lg ${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-white to-gray-50'} shadow-2xl border-2`}
        style={{ borderColor: netWorthColor }}>
        <div className="text-center">
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
            Your Net Worth
          </p>
          <div className="flex items-center justify-center gap-3">
            {netWorth >= 0 ? (
              <TrendingUp className="w-12 h-12" style={{ color: netWorthColor }} />
            ) : (
              <TrendingDown className="w-12 h-12" style={{ color: netWorthColor }} />
            )}
            <p className="text-5xl font-bold" style={{ color: netWorthColor }}>
              {formatCurrency(netWorth, selectedCountry)}
            </p>
          </div>
          <p className={`text-sm mt-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Total Assets: {formatCurrency(totalAssets, selectedCountry)} - Total Liabilities: {formatCurrency(totalLiabilities, selectedCountry)}
          </p>
        </div>

        {/* Financial Health Score */}
        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Financial Health Score
            </span>
            <span className="text-lg font-bold" style={{ color: healthScore.color }}>
              {healthScore.score}/100 - {healthScore.rating}
            </span>
          </div>
          <div className={`w-full h-3 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="h-3 rounded-full transition-all"
              style={{
                width: `${healthScore.score}%`,
                backgroundColor: healthScore.color
              }}
            />
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`p-4 sm:p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Assets
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-500 mt-1 break-words">
                {formatCurrency(totalAssets, selectedCountry)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-green-500 opacity-20 flex-shrink-0" />
          </div>
        </div>

        <div className={`p-4 sm:p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Liabilities
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-500 mt-1 break-words">
                {formatCurrency(totalLiabilities, selectedCountry)}
              </p>
            </div>
            <TrendingDown className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-red-500 opacity-20 flex-shrink-0" />
          </div>
        </div>

        <div className={`p-4 sm:p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Debt-to-Asset Ratio
              </p>
              <p className={`text-lg sm:text-xl lg:text-2xl font-bold mt-1 ${
                debtToAssetRatio < 30 ? 'text-green-500' :
                debtToAssetRatio < 60 ? 'text-yellow-500' : 'text-red-500'
              }`}>
                {debtToAssetRatio.toFixed(1)}%
              </p>
            </div>
            <Activity className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-blue-500 opacity-20 flex-shrink-0" />
          </div>
        </div>

        <div className={`p-4 sm:p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Health Rating
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold mt-1 break-words" style={{ color: healthScore.color }}>
                {healthScore.rating}
              </p>
            </div>
            {healthScore.rating === 'Excellent' || healthScore.rating === 'Good' ? (
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 opacity-20 flex-shrink-0" style={{ color: healthScore.color }} />
            ) : (
              <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 opacity-20 flex-shrink-0" style={{ color: healthScore.color }} />
            )}
          </div>
        </div>
      </div>

      {/* Breakdown Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Assets Breakdown */}
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-green-500" />
            Asset Breakdown
          </h3>

          {assetBreakdownData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={assetBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {assetBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value, selectedCountry)}
                    contentStyle={{
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      color: isDark ? '#ffffff' : '#000000'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-2 mt-4">
                {assetBreakdownData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className="text-sm font-semibold">
                      {formatCurrency(item.value, selectedCountry)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Wallet className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                No assets recorded
              </p>
            </div>
          )}
        </div>

        {/* Liabilities Breakdown */}
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <TrendingDown className="w-6 h-6 text-red-500" />
            Liability Breakdown
          </h3>

          {liabilitiesBreakdownData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={liabilitiesBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {liabilitiesBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value, selectedCountry)}
                    contentStyle={{
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      color: isDark ? '#ffffff' : '#000000'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-2 mt-4">
                {liabilitiesBreakdownData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {item.name}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-red-500">
                      {formatCurrency(item.value, selectedCountry)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                No liabilities - Great job!
              </p>
            </div>
          )}
        </div>

        {/* Net Worth Comparison */}
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-500" />
            Assets vs Liabilities
          </h3>

          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={netWorthBreakdownData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis
                dataKey="name"
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
              />
              <YAxis
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
                tickFormatter={(value) => formatChartValue(value, getCurrencySymbol(selectedCountry))}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value, selectedCountry)}
                contentStyle={{
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  color: isDark ? '#ffffff' : '#000000'
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {netWorthBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Net Worth:</span>
              <span className="font-bold" style={{ color: netWorthColor }}>
                {formatCurrency(netWorth, selectedCountry)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Difference:</span>
              <span className="font-bold" style={{ color: netWorthColor }}>
                {formatCurrency(Math.abs(netWorth), selectedCountry)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <h3 className="text-xl font-bold mb-4">Detailed Breakdown</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Assets Detail */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-500 flex items-center gap-2">
              <Home className="w-5 h-5" />
              Assets by Class
            </h4>
            <div className="space-y-2">
              {assetBreakdownData.map((asset, index) => (
                <div key={index} className="flex justify-between p-3 rounded-lg" style={{ backgroundColor: isDark ? '#1f2937' : '#f9fafb' }}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: asset.color }}
                    />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{asset.name}</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(asset.value, selectedCountry)}</span>
                </div>
              ))}
              {assetBreakdownData.length === 0 && (
                <div className="flex justify-between p-3 rounded-lg" style={{ backgroundColor: isDark ? '#1f2937' : '#f9fafb' }}>
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>No assets</span>
                  <span className="font-semibold">{formatCurrency(0, selectedCountry)}</span>
                </div>
              )}
              <div className="flex justify-between p-3 rounded-lg bg-green-500 bg-opacity-10 border border-green-500">
                <span className="font-bold text-green-500">Total Assets</span>
                <span className="font-bold text-green-500">{formatCurrency(totalAssets, selectedCountry)}</span>
              </div>
            </div>
          </div>

          {/* Liabilities Detail */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-red-500 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Liabilities
            </h4>
            <div className="space-y-2">
              {Object.values(groupLiabilitiesByType(liabilities, displayCurrency, exchangeRates)).map((group, index) => (
                <div key={index} className="flex justify-between p-3 rounded-lg" style={{ backgroundColor: isDark ? '#1f2937' : '#f9fafb' }}>
                  <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{group.type}</span>
                  <span className="font-semibold text-red-500">{formatCurrency(group.total, selectedCountry)}</span>
                </div>
              ))}
              {liabilities.length === 0 && (
                <div className="flex justify-between p-3 rounded-lg" style={{ backgroundColor: isDark ? '#1f2937' : '#f9fafb' }}>
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>No liabilities</span>
                  <span className="font-semibold text-green-500">{formatCurrency(0, selectedCountry)}</span>
                </div>
              )}
              <div className="flex justify-between p-3 rounded-lg bg-red-500 bg-opacity-10 border border-red-500">
                <span className="font-bold text-red-500">Total Liabilities</span>
                <span className="font-bold text-red-500">{formatCurrency(totalLiabilities, selectedCountry)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      {(debtToAssetRatio > 50 || netWorth < 0 || totalLiabilities > totalInvestments) && (
        <div className={`p-6 rounded-lg ${isDark ? 'bg-orange-900 bg-opacity-20' : 'bg-orange-50'} border-2 border-orange-500`}>
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-orange-500">
            <AlertCircle className="w-6 h-6" />
            Financial Insights
          </h3>
          <div className="space-y-2 text-sm">
            {debtToAssetRatio > 50 && (
              <p className={isDark ? 'text-orange-300' : 'text-orange-700'}>
                • Your debt-to-asset ratio is {debtToAssetRatio.toFixed(1)}%. Consider focusing on debt reduction.
              </p>
            )}
            {netWorth < 0 && (
              <p className={isDark ? 'text-orange-300' : 'text-orange-700'}>
                • Your liabilities exceed your assets. Prioritize building your emergency fund and reducing high-interest debt.
              </p>
            )}
            {totalLiabilities > totalInvestments && totalLiabilities > 0 && (
              <p className={isDark ? 'text-orange-300' : 'text-orange-700'}>
                • Your liabilities exceed your investments. Consider increasing your investment contributions while managing debt.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NetWorthDashboard;
